/**
 * Site header — compact burger menu when nav overlaps logo.
 */
const COMPACT_NAV_GAP = 16;
const BURGER_TOGGLE_WIDTH = 46;

export class SiteHeader {
	/**
	 * @param {HTMLElement} header Root `.site-header` element.
	 */
	constructor( header ) {
		this.header = header;
		this.inner = header.querySelector( '.site-header-inner' );
		this.brand = header.querySelector( '.site-header-brand' );
		this.nav = header.querySelector( '.site-header-nav' );
		this.menuToggle = header.querySelector( '.site-header-menu-toggle' );

		if ( ! this.inner || ! this.brand || ! this.nav || ! this.menuToggle ) {
			return;
		}

		this.groupItems = header.querySelectorAll( '.site-header-nav-item--group' );
		this.linkSections = header.querySelectorAll( '[data-header-accordion]' );
		this.language = header.querySelector( '.site-header-language' );
		this.languageToggle = this.language?.querySelector( '.site-header-language-toggle' ) ?? null;

		this.bind();
		this.updateCompactMode();
	}

	bind() {
		this.menuToggle.addEventListener( 'click', () => {
			this.toggleMenu();
		} );

		this.languageToggle?.addEventListener( 'click', ( event ) => {
			event.stopPropagation();
			this.toggleLanguageDropdown();
		} );

		document.addEventListener( 'click', ( event ) => {
			if ( this.language && ! this.language.contains( event.target ) ) {
				this.closeLanguageDropdown();
			}
		} );

		this.groupItems.forEach( ( item ) => {
			const trigger = item.querySelector( '.site-header-nav-group-toggle' );

			if ( ! trigger ) {
				return;
			}

			trigger.addEventListener( 'click', () => {
				if ( ! this.isCompactNav() ) {
					return;
				}

				this.toggleAccordionItem( item, trigger );
			} );
		} );

		this.linkSections.forEach( ( section ) => {
			const trigger = section.querySelector( '.site-header-panel-links-section-toggle' );

			if ( ! trigger ) {
				return;
			}

			trigger.addEventListener( 'click', () => {
				if ( ! this.isCompactNav() ) {
					return;
				}

				this.toggleAccordionItem( section, trigger );
			} );
		} );

		this.resizeObserver = new ResizeObserver( () => {
			this.updateCompactMode();
		} );

		this.resizeObserver.observe( this.inner );

		const siteTop = this.header.closest( '.site-top' );

		if ( siteTop && siteTop !== this.inner ) {
			this.resizeObserver.observe( siteTop );
		}

		window.addEventListener( 'resize', () => {
			this.updateCompactMode();
		} );

		document.addEventListener( 'strider:site-top-change', () => {
			this.updateHeaderHeight();
		} );

		document.addEventListener( 'keydown', ( event ) => {
			if ( 'Escape' !== event.key ) {
				return;
			}

			if ( this.isLanguageOpen() ) {
				this.closeLanguageDropdown();
				return;
			}

			if ( this.isMenuOpen() ) {
				this.closeMenu();
			}
		} );
	}

	isLanguageOpen() {
		return this.language?.classList.contains( 'is-open' ) ?? false;
	}

	toggleLanguageDropdown() {
		if ( ! this.language || ! this.languageToggle ) {
			return;
		}

		if ( this.isLanguageOpen() ) {
			this.closeLanguageDropdown();
			return;
		}

		this.language.classList.add( 'is-open' );
		this.languageToggle.setAttribute( 'aria-expanded', 'true' );
	}

	closeLanguageDropdown() {
		if ( ! this.language || ! this.languageToggle ) {
			return;
		}

		this.language.classList.remove( 'is-open' );
		this.languageToggle.setAttribute( 'aria-expanded', 'false' );
	}

	isCompactNav() {
		return this.header.classList.contains( 'is-compact-nav' );
	}

	isMenuOpen() {
		return this.header.classList.contains( 'is-menu-open' );
	}

	needsCompactNav() {
		const gaps = COMPACT_NAV_GAP * 2;
		const desktopAvailable =
			this.inner.clientWidth - this.brand.offsetWidth - gaps;

		if ( this.isCompactNav() ) {
			return this.nav.scrollWidth > desktopAvailable;
		}

		const toggleWidth = Math.max( this.menuToggle.offsetWidth, BURGER_TOGGLE_WIDTH );
		const compactAvailable =
			this.inner.clientWidth - this.brand.offsetWidth - toggleWidth - gaps;

		return this.nav.scrollWidth > compactAvailable;
	}

	updateCompactMode() {
		const needsCompact = this.needsCompactNav();

		if ( needsCompact ) {
			this.header.classList.add( 'is-compact-nav' );
		} else {
			this.header.classList.remove( 'is-compact-nav' );
			this.closeMenu();
		}

		this.updateHeaderHeight();
	}

	updateHeaderHeight() {
		const siteTop = this.header.closest( '.site-top' );
		const headerHeight = this.header.offsetHeight;
		const topHeight = siteTop ? siteTop.offsetHeight : headerHeight;

		this.header.style.setProperty( '--site-header-height', `${ headerHeight }px` );

		if ( siteTop ) {
			siteTop.style.setProperty( '--site-top-height', `${ topHeight }px` );
		}

		document.documentElement.style.setProperty( '--site-top-height', `${ topHeight }px` );
		document.documentElement.style.setProperty( '--site-header-height', `${ headerHeight }px` );
	}

	toggleMenu() {
		if ( this.isMenuOpen() ) {
			this.closeMenu();
			return;
		}

		this.header.classList.add( 'is-menu-open' );
		this.menuToggle.setAttribute( 'aria-expanded', 'true' );
		document.body.classList.add( 'is-header-menu-open' );
	}

	closeMenu() {
		this.header.classList.remove( 'is-menu-open' );
		this.menuToggle.setAttribute( 'aria-expanded', 'false' );
		document.body.classList.remove( 'is-header-menu-open' );
		this.closeLanguageDropdown();
		this.closeAllAccordions();
	}

	/**
	 * @param {HTMLElement} item
	 * @param {HTMLButtonElement} trigger
	 */
	toggleAccordionItem( item, trigger ) {
		const isOpen = item.classList.contains( 'is-accordion-open' );

		item.classList.toggle( 'is-accordion-open', ! isOpen );
		trigger.setAttribute( 'aria-expanded', String( ! isOpen ) );
	}

	closeAllAccordions() {
		this.header.querySelectorAll( '.is-accordion-open' ).forEach( ( item ) => {
			item.classList.remove( 'is-accordion-open' );
			item.querySelector( '[aria-expanded="true"]' )?.setAttribute( 'aria-expanded', 'false' );
		} );
	}
}
